/* Email side effects of HR and billing actions — mirrors api/internal/api/events.go. */
import type * as T from "../types";
import type { Ctx } from "./ctx";
import { employeeRecipient, LEAVE_NAMES, LETTER_AR, moneyIn, moneySAR, periodLabels, usersWithRoles } from "./mailer";
import type { PaymentRec } from "./state";

const leaveNames = (t: string): [string, string] => LEAVE_NAMES[t] ?? [t + " leave", t];

export function evLeaveSubmitted(c: Ctx, l: T.Leave, requesterId: string) {
  const e = c.state.employees.find((x) => x.id === l.employeeId);
  if (!e) return;
  const [en, ar] = leaveNames(l.type);
  for (const rc of usersWithRoles(c, c.tid, "owner", "hr")) {
    if (rc.userId === requesterId) continue;
    c.mail.notify(c.tid, "leave-submitted", rc, { Employee: e.nameEn, EmployeeAr: e.nameAr || e.nameEn, LeaveType: en, LeaveTypeAr: ar, From: l.from, To: l.to, Days: String(l.days), Note: l.note, Link: "/#/approvals" },
      `leave-submitted:${c.tid}:${l.id}:${rc.userId}`);
  }
}

export function evLeaveDecided(c: Ctx, l: T.Leave) {
  const rc = employeeRecipient(c, c.state, l.employeeId);
  if (!rc) return;
  const [en, ar] = leaveNames(l.type);
  const tpl = l.status === "Approved" ? "leave-approved" : "leave-rejected";
  c.mail.notify(c.tid, tpl, rc, { LeaveType: en, LeaveTypeAr: ar, From: l.from, To: l.to, Days: String(l.days), Link: "/#/ess/leave" }, `${tpl}:${c.tid}:${l.id}`);
}

export function evRequestDecided(c: Ctx, kind: string, kindAr: string, id: number, empId: number, summary: string, approved: boolean) {
  const rc = employeeRecipient(c, c.state, empId);
  if (!rc) return;
  const tpl = approved ? "request-approved" : "request-rejected";
  c.mail.notify(c.tid, tpl, rc, { Kind: kind, KindAr: kindAr, Summary: summary, Ref: "#" + id, Link: "/#/ess" }, `${tpl}:${c.tid}:${kind}:${id}`);
}

export function evLetterIssued(c: Ctx, l: T.Letter) {
  const rc = employeeRecipient(c, c.state, l.employeeId);
  if (!rc) return;
  c.mail.notify(c.tid, "letter-issued", rc, { Letter: l.kind, LetterAr: LETTER_AR[l.kind] ?? l.kind, To: l.to, Ref: "#" + l.id, Link: "/#/ess/letters" }, `letter-issued:${c.tid}:${l.id}`);
}

export function evPayrollApproved(c: Ctx, v: T.PayrollRunView, by: string) {
  const [en, ar] = periodLabels(v.run.period);
  for (const rc of usersWithRoles(c, c.tid, "owner", "accountant")) {
    c.mail.notify(c.tid, "payroll-approved", rc, { Period: en, PeriodAr: ar, Headcount: String(v.totals.headcount), Gross: moneyIn(v.totals.gross, c.cur), Net: moneyIn(v.totals.net, c.cur), ApprovedBy: by, Link: "/#/payroll/run" },
      `payroll-approved:${c.tid}:${v.run.period}:${rc.userId}`);
  }
}

export function evPayslips(c: Ctx, period: string, payDate: string, slips: T.Payslip[]) {
  const [en, ar] = periodLabels(period);
  for (const sl of slips) {
    const rc = employeeRecipient(c, c.state, sl.employeeId);
    if (!rc) continue;
    c.mail.notify(c.tid, "payslip-available", rc, { Period: en, PeriodAr: ar, Net: moneyIn(sl.net, c.cur), PayDate: payDate, Link: "/#/ess/payslips" }, `payslip:${c.tid}:${period}:${sl.employeeId}`);
  }
}

export function evResignationSubmitted(c: Ctx, o: T.Offboarding, actorId: string) {
  const e = c.state.employees.find((x) => x.id === o.employeeId);
  if (!e) return;
  for (const rc of usersWithRoles(c, c.tid, "owner", "hr")) {
    if (rc.userId === actorId) continue;
    c.mail.notify(c.tid, "resignation-submitted", rc, { Employee: e.nameEn, EmployeeAr: e.nameAr || e.nameEn, LastDay: o.lastDay, Submitted: o.submitted, Link: "/#/lifecycle" }, `resignation:${c.tid}:${o.id}:${rc.userId}`);
  }
}

export function evResignationAcknowledged(c: Ctx, o: T.Offboarding) {
  const rc = employeeRecipient(c, c.state, o.employeeId);
  if (!rc) return;
  c.mail.notify(c.tid, "resignation-acknowledged", rc, { LastDay: o.lastDay, Link: "/#/ess" }, `resignation-ack:${c.tid}:${o.id}`);
}

export function evSettlementReady(c: Ctx, o: T.Offboarding, net: number) {
  const e = c.state.employees.find((x) => x.id === o.employeeId);
  if (!e) return;
  const data = { Employee: e.nameEn, EmployeeAr: e.nameAr || e.nameEn, LastDay: o.lastDay, Net: moneyIn(net, c.cur), Link: "/#/lifecycle" };
  let rcs = usersWithRoles(c, c.tid, "hr");
  if (!rcs.length) rcs = usersWithRoles(c, c.tid, "owner");
  const emp = employeeRecipient(c, c.state, o.employeeId);
  if (emp) rcs = [...rcs, emp];
  for (const rc of rcs) c.mail.notify(c.tid, "settlement-ready", rc, data, `settlement:${c.tid}:${o.id}:${rc.email}`);
}

export function evPaymentSubmitted(c: Ctx, p: PaymentRec) {
  const tid = p.tenantId ?? "";
  for (const rc of usersWithRoles(c, tid, "owner")) {
    c.mail.notify(tid, "payment-received", rc, { Ref: p.ref, Amount: moneySAR(p.total), Plan: p.plan, Seats: String(p.seats), Date: p.date, Link: "/#/billing" }, `payment-received:${p.id}:${rc.userId}`);
  }
  for (const rc of usersWithRoles(c, "platform", "admin")) {
    c.mail.notify("platform", "payment-review", rc, { Company: p.tenant, CR: p.cr, Ref: p.ref, Amount: moneySAR(p.total), Paid: moneySAR(p.paid), Bank: p.bank, Sender: p.sender, Date: p.date, Link: "/#/admin/payments" },
      `payment-review:${p.id}:${rc.userId}`);
  }
}

export function evPaymentVerified(c: Ctx, p: PaymentRec) {
  const st = c.stateFor(p.tenantId);
  if (!st) return;
  const [cycle, cycleAr] = p.cycle === "y" ? ["Yearly", "سنوي"] : ["Monthly", "شهري"];
  for (const rc of usersWithRoles(c, st.tenantId, "owner")) {
    c.mail.notify(st.tenantId, "payment-verified", rc, { Ref: p.ref, Invoice: p.id, Plan: p.plan, Cycle: cycle, CycleAr: cycleAr, Seats: String(p.seats), Amount: moneySAR(p.amount), Vat: moneySAR(p.vat),
      Total: moneySAR(p.total), ActiveUntil: st.subscription.activeUntil ?? "", VatNo: st.settings.tenant.vat, Link: "/#/billing" }, `payment-verified:${p.id}:${rc.userId}`);
  }
}

export function evPaymentRejected(c: Ctx, p: PaymentRec) {
  if (!p.tenantId) return;
  for (const rc of usersWithRoles(c, p.tenantId, "owner")) {
    c.mail.notify(p.tenantId, "payment-rejected", rc, { Ref: p.ref, Reason: p.reason ?? "", Link: "/#/billing" }, `payment-rejected:${p.id}:${rc.userId}`);
  }
}
